import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, IconButton, Tooltip, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';

import { CheckedMode, InfiniteQuery, UseCheckedState } from 'types/app';
import {
  AnalyticsProfile,
  CreateClientInvitationInput,
  OrganicInsightsSortFields,
  SortDirections,
  useCreateClientInvitationMutation,
  UserType,
} from 'api';
import { DataTable, DataTableActions, DataTableColumn, SocialAvatar } from 'components';
import { useNotify, useProtectedClient } from 'hooks';

import { SortAction, SortParam } from 'components/DataTable';
import { getRoles } from 'features/insights/helpers';

interface Props extends Partial<UseCheckedState>, InfiniteQuery<AnalyticsProfile> {
  sortBy: SortParam;
  onSort: SortAction;
}

const EmployeesTable: FC<Props> = ({
  sentinelRef,
  results,
  isExhausted,
  isInitialLoad,
  sortBy,
  onSort,
  ...rest
}) => {
  const { t } = useTranslation();

  const notify = useNotify();
  const client = useProtectedClient();
  const hasEmails = results.some(profile => profile?.email_address?.length);
  const { setCheckedState, checkedMode, checkedIds } = rest;

  const isAllChecked = checkedMode !== CheckedMode.OFF;
  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const { mutate: sendInvites, isLoading } = useCreateClientInvitationMutation({
    onError: notify.mutationError,
    onSuccess: () => {
      notify.info({ message: t('invites:inviteSuccess') });
    },
  });

  const payload: CreateClientInvitationInput = {
    department_id: '',
    location_id: '',
    email_addresses: checkedIds,
    subject: t('insights:subject', { clientTitle: client.title }),
    message: t('insights:message'),
    user_type: UserType.User,
  };

  const columns: DataTableColumn<AnalyticsProfile>[] = [
    {
      width: 48,
      headerCell: {
        padding: 'checkbox',
        render: (
          <Checkbox
            disabled={!hasEmails}
            checked={isAllChecked}
            onChange={handleAllCheckedChange}
          />
        ),
      },
      bodyCell: {
        padding: 'checkbox',
        render: profile => (
          <Checkbox
            disabled={!profile.email_address}
            checked={checkedIds.includes(profile.email_address)}
            onChange={event => onClickCheckbox(profile.email_address, event.target.checked)}
          />
        ),
      },
    },
    {
      width: 56,
      headerCell: {
        render: t('common:name'),
      },
      bodyCell: {
        render: profile => (
          <SocialAvatar name={profile.name} image={profile.network_profile_image} size={48} />
        ),
      },
    },
    {
      width: 300,
      headerCell: {
        render: null,
      },
      bodyCell: {
        render: profile => (
          <Typography title={profile.name} variant="subtitle2" noWrap>
            {profile.name}
          </Typography>
        ),
      },
    },
    {
      width: 120,
      headerCell: {
        sort: {
          field: OrganicInsightsSortFields.Engagements,
          direction: SortDirections.Desc,
        },
        render: t('insights:engagements'),
      },
      bodyCell: {
        render: profile => (
          <Typography variant="subtitle2" align="center">
            {profile.engagement_summary.engagements}
          </Typography>
        ),
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('insights:network_bio'),
      },
      bodyCell: {
        render: profile => (
          <Typography noWrap title={profile.network_bio}>
            {profile.network_bio}
          </Typography>
        ),
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('common:role'),
      },
      bodyCell: {
        render: profile => {
          const roles = getRoles(profile);
          return (
            <Typography title={roles} noWrap>
              {roles}
            </Typography>
          );
        },
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('user:email'),
      },
      bodyCell: {
        render: profile => (
          <Typography title={profile.email_address} noWrap>
            {profile.email_address}
          </Typography>
        ),
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('common:region'),
      },
      bodyCell: {
        render: ({ location }) => (
          <Typography title={location} noWrap>
            {location}
          </Typography>
        ),
      },
    },
  ];

  const actions: DataTableActions<AnalyticsProfile> = profile => {
    if (!profile.email_address) {
      return null;
    }

    return (
      <Grid container alignItems="center">
        <Grid>
          <Tooltip title={t('common:invite')}>
            <IconButton onClick={onClickSend} disabled={isLoading} size="large">
              <SendIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    );
  };

  function handleAllCheckedChange(event: ChangeEvent<HTMLInputElement>) {
    setCheckedState(event.target.checked ? CheckedMode.PAGE : CheckedMode.OFF);
  }

  function onClickSend() {
    sendInvites({ data: payload });
  }

  function onClickCheckbox(checkedId: string, checked: boolean) {
    let nextCheckedIds = [];

    if (checkedMode) {
      setCheckedState(CheckedMode.OFF);
      nextCheckedIds = results.map(item => item.email_address).filter(id => id !== checkedId);
    } else if (checked) {
      nextCheckedIds = checkedIds.slice();
      nextCheckedIds.push(checkedId);
    } else {
      nextCheckedIds = checkedIds.filter(id => id !== checkedId);
    }

    setCheckedState(nextCheckedIds);
  }

  return (
    <DataTable
      pagination={pagination}
      data={results}
      columns={columns}
      actions={actions}
      rowSelection={null}
      sortBy={sortBy}
      onSort={onSort}
    />
  );
};

export default EmployeesTable;
